import { BaseGrid } from './BaseGrid';
import { Cell } from './Cell';
import { GridType } from './GridType';
import { IsometricMath } from './IsometricMath';

export interface IsometricGridConfig {
  rows: number;
  cols: number;
  allowDiagonal?: boolean;
}

export class IsometricGrid extends BaseGrid {
  public allowDiagonal: boolean;

  constructor(config: IsometricGridConfig) {
    super({
      rows: config.rows,
      cols: config.cols,
      gridType: GridType.ISOMETRIC
    });
    this.allowDiagonal = config.allowDiagonal ?? false;
  }

  getNeighbors(cell: Cell): Cell[] {
    const neighbors: Cell[] = [];

    const cardinalDirections = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1]
    ];

    const diagonalDirections = [
      [-1, -1],
      [-1, 1],
      [1, -1],
      [1, 1]
    ];

    const directions = this.allowDiagonal
      ? [...cardinalDirections, ...diagonalDirections]
      : cardinalDirections;

    for (const [dRow, dCol] of directions) {
      const newRow = cell.row + dRow;
      const newCol = cell.col + dCol;
      const neighbor = this.getCell(newRow, newCol);

      if (neighbor && neighbor.isWalkable) {
        if (this.allowDiagonal && Math.abs(dRow) === 1 && Math.abs(dCol) === 1) {
          const horizontalCell = this.getCell(cell.row, newCol);
          const verticalCell = this.getCell(newRow, cell.col);
          if (horizontalCell?.isWalkable || verticalCell?.isWalkable) {
            neighbors.push(neighbor);
          }
        } else {
          neighbors.push(neighbor);
        }
      }
    }

    return neighbors;
  }

  calculateDistance(cellA: Cell, cellB: Cell): number {
    if (this.allowDiagonal) {
      return IsometricMath.chebyshevDistance(cellA.col, cellA.row, cellB.col, cellB.row);
    }
    return IsometricMath.manhattanDistance(cellA.col, cellA.row, cellB.col, cellB.row);
  }

  clone(): IsometricGrid {
    const newGrid = new IsometricGrid({
      rows: this.rows,
      cols: this.cols,
      allowDiagonal: this.allowDiagonal
    });

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const originalCell = this.cells[row][col];
        const newCell = newGrid.getCellAt(row, col);
        newCell.type = originalCell.type;
        newCell.weight = originalCell.weight;
      }
    }

    return newGrid;
  }
}
